import { throttle } from 'es-toolkit';
import { result, task, Unit } from 'true-myth';
import type { Result, Task } from 'true-myth';
import { ChainedError } from './task';

/**
 * Polls `fn` every `ms` once the previous poll settles, serving the last successful value.
 *
 * `fn` must not throw synchronously or return a Task that never settles: a sync throw escapes the
 * poll timer and a hung Task stops rescheduling. Do fallible work inside `task.tryOrElse` or `tryTask`.
 */
export const withRefetchInterval = <T, E>(fn: () => Task<T, E>, ms: number) => {
  // The most recent *successful* fetch task, seeded with the initial fetch. Awaiting it on a cold
  // start joins the in-flight startup fetch instead of firing a duplicate request, and a failed
  // background poll never replaces it, so we keep serving the last good value.
  let value: Task<T, E>;

  // Self-scheduling timeout (not setInterval) so the next poll is only queued once the current one
  // settles — a slow fetch can never stack up overlapping requests.
  let timeout: NodeJS.Timeout,
    stopped = false;

  const refetch = () => {
    const next = () => {
      // Cancel any pending poll first so overlapping chains (e.g. a manual refetch racing a
      // scheduled one) collapse to a single live timer that stop() can always clear.
      clearTimeout(timeout);
      if (!stopped) timeout = setTimeout(refetch, ms);
    };

    const fetched = fn()
      .inspect(() => {
        value = fetched;
      })
      .inspect(next)
      .inspectRejected(next);

    return fetched;
  };

  value = refetch();

  return {
    value: () => value,
    refetch,
    stop: () => {
      stopped = true;
      clearTimeout(timeout);
    },
  } as const;
};

export class BufferFullError extends ChainedError {
  override readonly name = 'BufferFullError';

  constructor(maxBufferLength: number) {
    super(`batch write dropped: buffer full (max ${maxBufferLength})`);
  }
}

// Accumulates writes in a buffer and flushes them to `consume` in batches when instructed; writes beyond `maxBufferLength` reject with a BufferFullError
export class BatchWriter<T, U, E> {
  private readonly buffer: Array<{ item: T; settle: (settled: Result<U, E>) => void }> = [];

  constructor(
    private readonly consume: (batch: Array<T>) => Task<U, E>,
    private readonly maxBufferLength = Infinity,
  ) {}

  public write(item: T): Task<U, E | BufferFullError> {
    if (this.buffer.length >= this.maxBufferLength) {
      return task.reject(new BufferFullError(this.maxBufferLength));
    }

    const { task: written, resolve, reject } = task.withResolvers<U, E>();

    this.buffer.push({ item, settle: (settled) => settled.match({ Ok: resolve, Err: reject }) });

    return written;
  }

  public flush(): Task<Unit, E> {
    const entries = this.buffer.splice(0); // note: synchronously remove from buffer before async processing to avoid dupes

    if (!entries.length) {
      return task.resolve(Unit);
    }

    return this.consume(entries.map(({ item }) => item))
      .inspect((value) => entries.forEach(({ settle }) => settle(result.ok(value))))
      .inspectRejected((error) => entries.forEach(({ settle }) => settle(result.err(error))))
      .map(() => Unit);
  }
}

// Flushes on every write through a leading and trailing throttle of `interval` ms
export class ThrottledBatchWriter<T, U, E> extends BatchWriter<T, U, E> {
  private readonly throttledFlush: () => void;

  constructor(
    consume: (batch: Array<T>) => Task<U, E>,
    interval: number,
    { maxBufferLength, onFlushError }: { maxBufferLength?: number; onFlushError?: (error: E) => void } = {},
  ) {
    super(consume, maxBufferLength);
    this.throttledFlush = throttle(() => {
      this.flush().inspectRejected((error) => onFlushError?.(error));
    }, interval);
  }

  public override write(item: T) {
    const written = super.write(item);
    this.throttledFlush();
    return written;
  }
}
